import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getEvent, registerForEvent } from '../../services/api';

// 活動介紹而家容許商戶入 HTML(例如 <a href> 連結),但呢個內容係喺公開嘅
// 報名頁面顯示俾任何訪客睇,唔可以直接 dangerouslySetInnerHTML 未經清洗
// 嘅內容——用 DOMPurify 淨係留低安全嘅標籤/屬性,砍走 <script>、on* 呢類
// 可以走到 JS 嘅嘢。
// 凡係 target="_blank" 嘅連結,強制加 rel="noopener noreferrer",防止
// reverse tabnabbing(新分頁可以透過 window.opener 改返原本頁面個網址)。
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const sanitizeDescriptionHtml = (html) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 'br', 'p', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('zh-TW', { dateStyle: 'medium', timeStyle: 'short' });
};

// 提交完之後,用一個隱藏 form 自動 POST 去綠界收銀台 —— 綠界 AioCheckOut
// 要求用 form POST(唔係 GET/AJAX),所以要真係 render 一個 form 出嚟再自動送出。
const submitToEcpay = (actionUrl, params) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = actionUrl;
  Object.entries(params).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const EventRegister = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    contact_phone: '',
    contact_email: '',
    residential_area: '',
    invoice_number: '',
    num_participants: 1,
    notes: '',
  });
  const [participants, setParticipants] = useState([{ child_name: '', birth_date: '', gender: '' }]);

  useEffect(() => {
    getEvent(slug)
      .then((res) => setEvent(res.data))
      .catch(() => setError('搵唔到呢個活動'))
      .finally(() => setLoadingEvent(false));
  }, [slug]);

  const handleNumParticipantsChange = (value) => {
    const num = Math.max(1, parseInt(value, 10) || 1);
    setFormData({ ...formData, num_participants: num });
    setParticipants((prev) => {
      const next = [...prev];
      while (next.length < num) next.push({ child_name: '', birth_date: '', gender: '' });
      return next.slice(0, num);
    });
  };

  const handleParticipantChange = (index, field, value) => {
    setParticipants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const hasParticipantNames = participants.some((p) => p.child_name.trim());
      const payload = {
        ...formData,
        ...(hasParticipantNames
          ? { participants: participants.map((p) => ({ child_name: p.child_name, birth_date: p.birth_date || null, gender: p.gender || '' })) }
          : {}),
      };
      const res = await registerForEvent(slug, payload);
      submitToEcpay(res.data.ecpay_action_url, res.data.ecpay_params);
      // submitToEcpay 會導航離開呢頁,唔使再 setSubmitting(false)
    } catch (err) {
      setError(err.response?.data?.error || '報名失敗,請稍後再試');
      setSubmitting(false);
    }
  };

  if (loadingEvent) {
    return <div className="max-w-md mx-auto p-6 text-center text-gray-400">載入緊活動資料...</div>;
  }

  if (!event) {
    return <div className="max-w-md mx-auto p-6 text-center text-gray-500">{error || '搵唔到呢個活動'}</div>;
  }

  const canRegister = event.registration_is_open && !event.is_full;

  return (
    <div className="max-w-md mx-auto p-6 bg-gray-50 min-h-screen pb-24">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="p-6">
          <h1 className="text-3xl font-black text-gray-800 mb-4">{event.name}</h1>
          <div className="space-y-1 text-base text-gray-600">
            {event.location && <p>📍 {event.location}</p>}
            <p>🗓️ {formatDateTime(event.event_date)}</p>
            {event.is_early_bird_active ? (
              <p>
                🐦 早鳥價 <span className="text-red-500 font-bold">NT${event.effective_price_per_person}</span>
                {' '}(原價 <span className="line-through">NT${event.price_per_person}</span>,
                {formatDateTime(event.early_bird_deadline)} 前報名先有)
              </p>
            ) : (
              <p>💰 每人 NT${event.effective_price_per_person}</p>
            )}
            {event.remaining_slots !== null && (
              <p className={event.remaining_slots <= 5 ? 'text-red-500 font-bold' : ''}>
                🎟️ 剩餘名額:{event.remaining_slots}
              </p>
            )}
          </div>
        </div>
      </div>

      {event.description && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-700 mb-3">活動詳情</h2>
            <div
              className="text-base text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(event.description) }}
            />
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="p-6">
          <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">支援付款方式</p>
          <div className="flex flex-col items-center gap-4">
            <img src="https://kakatw.com/LinePay.jpg" alt="LINE Pay" className="w-full h-auto object-contain rounded-xl" />
            <img src="https://kakatw.com/PXPayPlus.jpg" alt="PX Pay Plus" className="w-full h-auto object-contain rounded-xl" />
            <img src="https://kakatw.com/JKOPay.jpg" alt="街口支付" className="w-full h-auto object-contain rounded-xl" />
          </div>
        </div>
      </div>

      {!canRegister ? (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 text-center text-gray-500">
          {event.is_full ? '名額已滿' : '報名未開放或已截止'}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">聯絡電話</label>
            <input
              required
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none font-bold"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">Email(選填)</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none font-bold"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">居住地區</label>
            <select
              required
              value={formData.residential_area}
              onChange={(e) => setFormData({ ...formData, residential_area: e.target.value })}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none font-bold"
            >
              <option value="" disabled>請選擇</option>
              <option value="taoyuan">桃園市</option>
              <option value="new_taipei">新北市</option>
              <option value="taipei">台北市</option>
              <option value="other">其他</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">參加人數</label>
            <select
              required
              value={formData.num_participants}
              onChange={(e) => handleNumParticipantsChange(e.target.value)}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none font-bold"
            >
              {Array.from(
                { length: Math.min(10, event.remaining_slots ?? 10) },
                (_, idx) => idx + 1
              ).map((n) => (
                <option key={n} value={n}>{n} 人</option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest">小朋友資料(選填)</label>
            {participants.map((p, i) => (
              // 之前 w-24/w-44 係打死嘅闊度,窄螢幕會逼到性別/生日畀擠出畫面。
              // 試過用 sm: breakpoint 分兩行/一行,但唔同裝置量出嚟嘅 CSS
              // viewport 闊度唔一致,索性唔理 breakpoint,永遠都係姓名獨立
              // 一行、性別+生日夾埋喺低一行 -- 唔理幾闊嘅螢幕都係咁,簡單直接。
              <div key={i} className="flex flex-col gap-2">
                <input
                  type="text"
                  placeholder={`第 ${i + 1} 位小朋友姓名`}
                  value={p.child_name}
                  onChange={(e) => handleParticipantChange(i, 'child_name', e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none"
                />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-400 mb-1">性別</label>
                    <select
                      value={p.gender}
                      onChange={(e) => handleParticipantChange(i, 'gender', e.target.value)}
                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-2 py-3 text-base focus:border-blue-500 outline-none"
                    >
                      <option value="">請選擇</option>
                      <option value="M">男</option>
                      <option value="F">女</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    {/* 淨係得個 date input 唔夠清楚,客人唔知道要入嘅係咩日期
                        (例如以為要入活動日期)-- 加個明確 label。 */}
                    <label className="block text-xs text-gray-400 mb-1">出生日期</label>
                    <input
                      type="date"
                      value={p.birth_date}
                      onChange={(e) => handleParticipantChange(i, 'birth_date', e.target.value)}
                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">發票載具/統編(選填)</label>
            <input
              type="text"
              placeholder="手機條碼載具或統一編號"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none"
            />
            <p className="text-sm text-gray-400 mt-1">留空 = 列印發票,活動當日專櫃領取</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-2">備註(選填)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 text-base focus:border-blue-500 outline-none"
              rows={2}
            />
          </div>

          <div className="text-right text-2xl font-black text-gray-800">
            總金額:NT${event.effective_price_per_person * formData.num_participants}
            {event.is_early_bird_active && <span className="ml-1 text-sm text-red-500 font-bold align-middle">早鳥價</span>}
          </div>

          {error && <p className="text-red-500 text-base font-bold">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl shadow-md disabled:bg-gray-300"
          >
            {submitting ? '處理緊...' : '前往付款'}
          </button>
        </form>
      )}
    </div>
  );
};

export default EventRegister;
